import React, { createContext, useCallback, useContext, useLayoutEffect, useState } from 'react';
import { ThemeMode, THEME_STORAGE_KEY, type ThemeModeValue } from './themeConstants';

interface ThemeContextValue {
  theme: ThemeModeValue;
  setTheme: (mode: ThemeModeValue) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function getSystemPreference(): 'light' | 'dark' {
  if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches
      ? ThemeMode.Dark
      : ThemeMode.Light;
  }
  return ThemeMode.Light;
}

function resolveEffectiveTheme(mode: ThemeModeValue): 'light' | 'dark' {
  if (mode === ThemeMode.System) {
    return getSystemPreference();
  }
  return mode;
}

function applyTheme(mode: ThemeModeValue): void {
  const effective = resolveEffectiveTheme(mode);
  document.documentElement.classList.toggle('dark', effective === ThemeMode.Dark);
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<ThemeModeValue>(() => {
    const stored = localStorage.getItem(THEME_STORAGE_KEY) as ThemeModeValue | null;
    if (stored && Object.values(ThemeMode).includes(stored)) {
      return stored;
    }
    return ThemeMode.System;
  });

  // Apply immediately (before paint) to prevent FOUC
  useLayoutEffect(() => {
    applyTheme(theme);
  }, [theme]);

  // Listen for OS preference changes when in system mode
  useLayoutEffect(() => {
    if (theme !== ThemeMode.System) return;
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;

    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => applyTheme(ThemeMode.System);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [theme]);

  const setTheme = useCallback((mode: ThemeModeValue) => {
    localStorage.setItem(THEME_STORAGE_KEY, mode);
    setThemeState(mode);
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return ctx;
}
